import React, { useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthProvider'
import { useContactModal } from '@/contexts/ContactModalProvider'
import { useAxios } from '@/contexts/AxiosProvider'
import { PricingSection } from '@/pages/(lobby)/PricingSection'
import { toast } from 'react-toastify'

export const OrgModal = () => {
  const { currentUser, currentOrg, showOrgModal, setShowOrgModal, setCurrentOrg } = useAuth()
  const { openContact } = useContactModal()
  const [orgName, setOrgName] = useState(currentOrg?.name || '')
  const [manageSub, setManageSub] = useState(false)
  const [isPaused, setIsPaused] = useState(
    !!currentOrg?.subscription?.paused_at ||
      currentOrg?.subscription?.scheduled_change?.action === 'pause',
  )
  const [isCanceled, setIsCanceled] = useState(
    currentOrg?.subscription?.scheduled_change?.action === 'cancel',
  )
  const axios = useAxios()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentOrg) return
    try {
      const res = await axios.put(`/orgs/${currentOrg.id}`, {
        name: orgName,
      })
      setCurrentOrg(res.data)
    } catch (err) {
      // no-op
    }
  }

  const pauseSubscription = async () => {
    if (!currentOrg?.subscriptionId) return
    try {
      await axios.post('/billing/pause', {
        subscriptionId: currentOrg.subscriptionId,
      })
      setIsPaused(true)
      toast.success('Subscription will be paused at the next billing period.')
    } catch (err) {
      // no-op
      toast.error('Failed to pause subscription.')
    }
  }

  const resumeSubscription = async () => {
    if (!currentOrg?.subscriptionId) return
    try {
      await axios.post('/billing/resume', {
        subscriptionId: currentOrg.subscriptionId,
      })
      setIsPaused(false)
      toast.success('Subscription resumed.')
    } catch (err) {
      // no-op
      toast.error('Failed to resume subscription.')
    }
  }

  const cancelSubscription = async () => {
    if (!currentOrg?.subscriptionId) return
    try {
      await axios.post(`/billing/cancel`, {
        subscriptionId: currentOrg.subscriptionId,
      })
      setIsCanceled(true)
      toast.success('Subscription will be canceled at the end of the current billing period.')
    } catch (error) {
      // no-op
      toast.error('Failed to cancel subscription.')
    }
  }

  return (
    <Dialog open={showOrgModal} onOpenChange={setShowOrgModal}>
      <DialogContent
        size="lg"
        className="max-h-[85vh] md:min-w-[600px] w-full max-w-2xl h-auto p-6 flex flex-col gap-6 overflow-y-auto dark:text-white noScrollbar"
      >
        <DialogTitle className="text-2xl font-bold">Organization Details</DialogTitle>

        {!manageSub ? (
          <>
            <div className="flex flex-col items-start gap-2">
              <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                Current Plan:{' '}
                <span className="text-black dark:text-white">{currentOrg?.level ?? 'N/A'}</span>
              </h2>
              <Button size="sm" variant="outline" onClick={() => setManageSub(true)}>
                Manage Subscription
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="orgName">Organization Name</Label>
                <Input
                  id="orgName"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  placeholder="Acme Inc."
                  maxLength={20}
                />
              </div>
              <Button type="submit" variant="secondary" className="w-full">
                Save Changes
              </Button>
            </form>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              {['admins', 'members'].map((type) => {
                const list =
                  type === 'admins'
                    ? currentOrg?.admins?.filter((user) => user.id !== currentOrg.founderId)
                    : currentOrg?.members
                return (
                  <div key={type}>
                    <h3 className="font-semibold text-sm uppercase mb-2">
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </h3>
                    {list?.length ? (
                      <div className="max-h-40 overflow-y-auto rounded border p-2">
                        <ul className="text-sm space-y-1">
                          {list.map((person) => (
                            <li key={person.id} className="flex items-center gap-2 min-h-[24px]">
                              {person.firstName} {person.lastName} ({person.email})
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm italic">No {type} found</p>
                    )}
                    <div className="mt-3 p-2 rounded border text-xs text-muted-foreground bg-gray-50 dark:bg-gray-800">
                      <strong>Coming soon:</strong> invite users, add/remove admins, and role management from this panel. For now, you can manage billing under “Manage Subscription.”
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="pt-4 flex justify-center">
              <Button variant="outline" onClick={() => openContact()}>
                Contact Support
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-4 items-center">
            <div className="w-full flex justify-center">
              <div className="w-full max-w-xl">
                <PricingSection buttonMode="select" compact />
              </div>
            </div>

            {(isPaused || isCanceled) && (
              <div className="text-sm text-center text-yellow-600 dark:text-yellow-400">
                {isPaused && 'This subscription is currently paused.'}
                {isCanceled &&
                  currentOrg?.subscription?.scheduled_change?.effective_at &&
                  `This subscription will be canceled on ${new Date(
                    currentOrg?.subscription?.scheduled_change?.effective_at,
                  ).toLocaleDateString()}.`}
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-3 justify-center items-center">
              <Button size="sm" variant="outline" onClick={() => setManageSub(false)}>
                Back
              </Button>
              {!isCanceled && (
                <div className="flex gap-2">
                  {isPaused ? (
                    <Button
                      variant="default"
                      className="w-full md:w-auto"
                      onClick={resumeSubscription}
                    >
                      Resume Subscription
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full md:w-auto"
                      onClick={pauseSubscription}
                    >
                      Pause Subscription
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    className="w-full md:w-auto"
                    onClick={cancelSubscription}
                  >
                    Cancel Subscription
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
